import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, X, Search, MoreVertical, Edit2, Trash2, Calendar, FileText, Upload, Car, Grid, List, AlertTriangle, Wrench } from 'lucide-react';
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle } from '../services/vehiclesApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';
import NavBar from './NavBar';

const Vehicles = () => {
    const navigate = useNavigate();
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [activeSection, setActiveSection] = useState('details'); // details, registration, insurance, service, accidents
    
    // Form states - Basic Details
    const [name, setName] = useState('');
    const [vehicleType, setVehicleType] = useState('4 Wheeler');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [serviceDates, setServiceDates] = useState([{ date: '', recurrence: 'none' }]);
    
    // Registration Details
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [engineNumber, setEngineNumber] = useState('');
    const [chassisNumber, setChassisNumber] = useState('');
    const [registrationValidUpto, setRegistrationValidUpto] = useState('');
    const [registrationDocument, setRegistrationDocument] = useState(null);
    
    // Insurance Details
    const [insuredBy, setInsuredBy] = useState('');
    const [insuranceFrom, setInsuranceFrom] = useState('');
    const [insuranceTo, setInsuranceTo] = useState('');
    const [premiumPaid, setPremiumPaid] = useState('');
    const [insuranceDocument, setInsuranceDocument] = useState(null);
    
    // Service History
    const [serviceHistory, setServiceHistory] = useState([]);
    
    // Accidents Record
    const [accidentsRecord, setAccidentsRecord] = useState([]);
    
    const [sharedWith, setSharedWith] = useState([]);
    
    // Vehicles data
    const [vehicles, setVehicles] = useState([]);
    
    const regFileInputRef = useRef(null);
    const insFileInputRef = useRef(null);

    const vehicleTypes = [
        '4 Wheeler',
        '2 Wheeler',
        'Cycle',
        'Heavy Vehicle',
        'Electric Scooter',
        'Segway',
        'Other'
    ];

    const recurrenceOptions = [
        { value: 'none', label: 'No Recurrence' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly (3 months)' },
        { value: 'halfyearly', label: 'Half Yearly (6 months)' },
        { value: 'yearly', label: 'Yearly' }
    ];

    useEffect(() => {
        const loadData = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const response = await fetch(`${apiUrl}/v1/families`, {
                    credentials: 'include'
                });
                
                if (!response.ok) {
                    throw new Error('Failed to load families');
                }
                
                const familiesData = await response.json();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
                
                if (familyToSelect && familyToSelect.members) {
                    const memberNames = familyToSelect.members
                        .filter(m => m && typeof m === 'object' && m.name)
                        .map(m => m.name);
                    setSharedWith(memberNames);
                }
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load vehicles when family changes
    useEffect(() => {
        const loadVehicles = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchVehicles(selectedFamily.id);
                setVehicles(response.items || []);
            } catch (error) {
                console.error('Error loading vehicles:', error);
            }
        };
        loadVehicles();
    }, [selectedFamily]);


    useEffect(() => {
        if (showAddModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showAddModal, showPremiumModal]);

    // Removed: No longer saving to localStorage, using API instead

    useEffect(() => {
        if (selectedFamily && selectedFamily.members) {
            const memberNames = selectedFamily.members
                .filter(m => m && typeof m === 'object' && m.name)
                .map(m => m.name);
            if (!editingVehicle) {
                setSharedWith(memberNames);
            }
        }
    }, [selectedFamily]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };


    const handleFileSelect = (type, e) => {
        const file = e.target.files[0];
        if (file) {
            const fileData = {
                name: file.name,
                file: file,
                url: URL.createObjectURL(file)
            };
            
            if (type === 'registration') {
                setRegistrationDocument(fileData);
            } else if (type === 'insurance') {
                setInsuranceDocument(fileData);
            }
        }
    };

    const handleAddVehicle = async () => {
        if (!name || !vehicleType) {
            alert('Please fill in all required fields');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const vehicleData = {
                name,
                vehicle_type: vehicleType,
                make: brand || null,
                model: model || null,
                year: purchaseDate ? new Date(purchaseDate).getFullYear() : null,
                license_plate: registrationNumber || null,
                vin: chassisNumber || null,
                insurance_provider: insuredBy || null,
                insurance_policy_number: engineNumber || null, // Assuming this is policy number
                purchase_date: purchaseDate || null,
                service_history: serviceDates.filter(sd => sd.date).map(sd => ({
                    date: sd.date,
                    recurrence: sd.recurrence
                })),
                insurance_from: insuranceFrom || null,
                insurance_to: insuranceTo || null,
                premium_paid: premiumPaid || null,
                registration_valid_upto: registrationValidUpto || null,
                accidents_record: accidentsRecord || [],
                shared_with: sharedWith,
                notes: null
            };

            if (editingVehicle) {
                const updatedVehicle = await updateVehicle(selectedFamily.id, editingVehicle.id, vehicleData);
                setVehicles(vehicles.map(v => v.id === editingVehicle.id ? updatedVehicle : v));
            } else {
                const createdVehicle = await createVehicle(selectedFamily.id, vehicleData);
                setVehicles([...vehicles, createdVehicle]);
            }

            resetForm();
        } catch (error) {
            console.error('Error saving vehicle:', error);
            alert(`Failed to save vehicle: ${error.message}`);
        }
    };

    const handleEditVehicle = (vehicle) => {
        setEditingVehicle(vehicle);
        setName(vehicle.name);
        setVehicleType(vehicle.vehicleType);
        setBrand(vehicle.brand || '');
        setModel(vehicle.model || '');
        setPurchaseDate(vehicle.purchaseDate || '');
        setServiceDates(vehicle.serviceDates && vehicle.serviceDates.length > 0 
            ? vehicle.serviceDates 
            : [{ date: '', recurrence: 'none' }]);
        
        setRegistrationNumber(vehicle.registrationNumber || '');
        setEngineNumber(vehicle.engineNumber || '');
        setChassisNumber(vehicle.chassisNumber || '');
        setRegistrationValidUpto(vehicle.registrationValidUpto || '');
        setRegistrationDocument(vehicle.registrationDocument || null);
        
        setInsuredBy(vehicle.insuredBy || '');
        setInsuranceFrom(vehicle.insuranceFrom || '');
        setInsuranceTo(vehicle.insuranceTo || '');
        setPremiumPaid(vehicle.premiumPaid || '');
        setInsuranceDocument(vehicle.insuranceDocument || null);
        
        setServiceHistory(vehicle.serviceHistory || []);
        setAccidentsRecord(vehicle.accidentsRecord || []);
        setSharedWith(vehicle.sharedWith || []);
        
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteVehicle = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vehicle?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteVehicle(selectedFamily.id, id);
            setVehicles(vehicles.filter(v => v.id !== id));
        } catch (error) {
            console.error('Error deleting vehicle:', error);
            alert(`Failed to delete vehicle: ${error.message}`);
        }
        
        setOpenMenuId(null);
    };

    const resetForm = () => {
        setName('');
        setVehicleType('4 Wheeler');
        setBrand('');
        setModel('');
        setPurchaseDate('');
        setServiceDates([{ date: '', recurrence: 'none' }]);
        setRegistrationNumber('');
        setEngineNumber('');
        setChassisNumber('');
        setRegistrationValidUpto('');
        setRegistrationDocument(null);
        setInsuredBy('');
        setInsuranceFrom('');
        setInsuranceTo('');
        setPremiumPaid('');
        setInsuranceDocument(null);
        setServiceHistory([]);
        setAccidentsRecord([]);
        setEditingVehicle(null);
        setShowAddModal(false);
        setActiveSection('details');
        
        if (selectedFamily && selectedFamily.members) {
            const memberNames = selectedFamily.members
                .filter(m => m && typeof m === 'object' && m.name)
                .map(m => m.name);
            setSharedWith(memberNames);
        }
        
        if (regFileInputRef.current) {
            regFileInputRef.current.value = '';
        }
        if (insFileInputRef.current) {
            insFileInputRef.current.value = '';
        }
    };

    const addServiceDate = () => {
        setServiceDates([...serviceDates, { date: '', recurrence: 'none' }]);
    };

    const removeServiceDate = (index) => {
        if (serviceDates.length > 1) {
            setServiceDates(serviceDates.filter((_, i) => i !== index));
        }
    };

    const updateServiceDate = (index, field, value) => {
        const newServiceDates = [...serviceDates];
        newServiceDates[index][field] = value;
        setServiceDates(newServiceDates);
    };

    const addServiceHistory = () => {
        setServiceHistory([...serviceHistory, {
            id: Date.now(),
            date: '',
            description: '',
            cost: '',
            serviceProvider: '',
            document: null
        }]);
    };

    const removeServiceHistory = (id) => {
        setServiceHistory(serviceHistory.filter(s => s.id !== id));
    };

    const updateServiceHistory = (id, field, value) => {
        setServiceHistory(serviceHistory.map(s => 
            s.id === id ? { ...s, [field]: value } : s
        ));
    };

    const addAccidentRecord = () => {
        setAccidentsRecord([...accidentsRecord, {
            id: Date.now(),
            date: '',
            location: '',
            type: '',
            severity: 'Minor',
            paymentType: 'Insurance',
            impactedParts: '',
            description: ''
        }]);
    };

    const removeAccidentRecord = (id) => {
        setAccidentsRecord(accidentsRecord.filter(a => a.id !== id));
    };

    const updateAccidentRecord = (id, field, value) => {
        setAccidentsRecord(accidentsRecord.map(a => 
            a.id === id ? { ...a, [field]: value } : a
        ));
    };

    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        return selectedFamily.members.filter(member => member && typeof member === 'object' && member.name);
    };

    const toggleMemberAccess = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (sharedWith.includes(memberName)) {
            setSharedWith(sharedWith.filter(m => m !== memberName));
        } else {
            setSharedWith([...sharedWith, memberName]);
        }
    };

    const getFilteredVehicles = () => {
        let filtered = [...vehicles];

        if (searchQuery) {
            filtered = filtered.filter(v => 
                v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.vehicleType.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.registrationNumber?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return filtered;
    };

    const getNextServiceDates = (vehicle) => {
        if (!vehicle.serviceDates || vehicle.serviceDates.length === 0) {
            return [];
        }

        const today = new Date();
        const allServiceDates = [];

        vehicle.serviceDates.forEach(serviceDate => {
            if (!serviceDate.date) return;

            if (serviceDate.recurrence === 'none') {
                allServiceDates.push({
                    date: serviceDate.date,
                    recurrence: 'none'
                });
            } else {
                let dueDate = new Date(serviceDate.date);
                
                while (dueDate <= today) {
                    switch (serviceDate.recurrence) {
                        case 'monthly':
                            dueDate.setMonth(dueDate.getMonth() + 1);
                            break;
                        case 'quarterly':
                            dueDate.setMonth(dueDate.getMonth() + 3);
                            break;
                        case 'halfyearly':
                            dueDate.setMonth(dueDate.getMonth() + 6);
                            break;
                        case 'yearly':
                            dueDate.setFullYear(dueDate.getFullYear() + 1);
                            break;
                        default:
                            break;
                    }
                }
                
                allServiceDates.push({
                    date: dueDate.toISOString().split('T')[0],
                    recurrence: serviceDate.recurrence
                });
            }
        });

        allServiceDates.sort((a, b) => new Date(a.date) - new Date(b.date));
        return allServiceDates;
    };

    const isInsuranceExpiring = (vehicle) => {
        if (!vehicle.insuranceTo) return false;
        const today = new Date();
        const insuranceEnd = new Date(vehicle.insuranceTo);
        const daysUntilExpiry = Math.ceil((insuranceEnd - today) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    };

    const isRegistrationExpiring = (vehicle) => {
        if (!vehicle.registrationValidUpto) return false;
        const today = new Date();
        const regEnd = new Date(vehicle.registrationValidUpto);
        const daysUntilExpiry = Math.ceil((regEnd - today) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <NavBar
                families={families}
                selectedFamily={selectedFamily}
                setSelectedFamily={setSelectedFamily}
                setShowPremiumModal={setShowPremiumModal}
                handleLogout={handleLogout}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Vehicles</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-3xl font-bold text-gray-900">Vehicles</h2>
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        {/* Search */}
                        <div className="relative flex-1 sm:flex-none">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search vehicles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent w-full sm:w-64"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="Grid view"
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="List view"
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <Plus className="w-5 h-5" />
                            Add Vehicle
                        </button>
                    </div>
                </div>

                {/* Vehicles List */}
                {getFilteredVehicles().length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No vehicles yet. Click "Add Vehicle" to get started!</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getFilteredVehicles().map((item) => {
                            const serviceDates = getNextServiceDates(item);
                            const insuranceExpiring = isInsuranceExpiring(item);
                            const registrationExpiring = isRegistrationExpiring(item);
                            const hasWarnings = insuranceExpiring || registrationExpiring;
                            
                            return (
                                <div key={item.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                    {item.vehicleType}
                                                </span>
                                                {hasWarnings && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        Expiring Soon
                                                    </span>
                                                )}
                                                {item.serviceHistory && item.serviceHistory.length > 0 && (
                                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center gap-1">
                                                        <Wrench className="w-3 h-3" />
                                                        {item.serviceHistory.length}
                                                    </span>
                                                )}
                                                {item.accidentsRecord && item.accidentsRecord.length > 0 && (
                                                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                                                        {item.accidentsRecord.length} Accident{item.accidentsRecord.length > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                                            {(item.brand || item.model) && (
                                                <p className="text-sm text-gray-600">
                                                    {item.brand && item.brand}{item.brand && item.model && ' - '}{item.model && item.model}
                                                </p>
                                            )}
                                            {item.registrationNumber && (
                                                <p className="text-xs text-gray-500 font-mono mt-1">{item.registrationNumber}</p>
                                            )}
                                        </div>

                                        {/* Menu */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === item.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleEditVehicle(item)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteVehicle(item.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="space-y-2 text-sm">
                                        {item.purchaseDate && (
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <Calendar className="w-4 h-4" />
                                                <span>Purchased: {new Date(item.purchaseDate).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        
                                        {insuranceExpiring && item.insuranceTo && (
                                            <div className="flex items-center gap-2 text-orange-600 font-medium">
                                                <AlertTriangle className="w-4 h-4" />
                                                <span>Insurance expires: {new Date(item.insuranceTo).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        
                                        {registrationExpiring && item.registrationValidUpto && (
                                            <div className="flex items-center gap-2 text-orange-600 font-medium">
                                                <AlertTriangle className="w-4 h-4" />
                                                <span>Registration expires: {new Date(item.registrationValidUpto).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        
                                        {serviceDates.length > 0 && (
                                            <div className="text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <Wrench className="w-4 h-4" />
                                                    <span>Next Service: {new Date(serviceDates[0].date).toLocaleDateString()}</span>
                                                </div>
                                                {serviceDates.length > 1 && (
                                                    <span className="text-xs ml-6">+{serviceDates.length - 1} more</span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Shared With */}
                                    {item.sharedWith && item.sharedWith.length > 0 && (
                                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
                                            <span className="text-xs text-gray-500">Shared with:</span>
                                            <div className="flex -space-x-2">
                                                {item.sharedWith.slice(0, 4).map((member, index) => (
                                                    <div key={index} className="relative">
                                                        <MemberAvatar name={member} size="xs" className="border-2 border-white" />
                                                    </div>
                                                ))}
                                                {item.sharedWith.length > 4 && (
                                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                        +{item.sharedWith.length - 4}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* List View */
                    <div className="space-y-3">
                        {getFilteredVehicles().map((item) => {
                            const insuranceExpiring = isInsuranceExpiring(item);
                            const registrationExpiring = isRegistrationExpiring(item);
                            const hasWarnings = insuranceExpiring || registrationExpiring;
                            
                            return (
                                <div key={item.id} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow">
                                    <div className="flex items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                    {item.vehicleType}
                                                </span>
                                                {hasWarnings && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded flex items-center gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        Expiring Soon
                                                    </span>
                                                )}
                                                {item.serviceHistory && item.serviceHistory.length > 0 && (
                                                    <span className="text-xs text-gray-600">{item.serviceHistory.length} Services</span>
                                                )}
                                                {item.accidentsRecord && item.accidentsRecord.length > 0 && (
                                                    <span className="text-xs text-red-600">{item.accidentsRecord.length} Accident{item.accidentsRecord.length > 1 ? 's' : ''}</span>
                                                )}
                                            </div>
                                            
                                            <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                                {item.brand && <span>{item.brand}</span>}
                                                {item.model && <span>{item.model}</span>}
                                                {item.registrationNumber && <span className="font-mono">{item.registrationNumber}</span>}
                                            </div>
                                            
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                                {item.purchaseDate && (
                                                    <span>Purchased: {new Date(item.purchaseDate).toLocaleDateString()}</span>
                                                )}
                                                {item.insuranceTo && (
                                                    <span className={insuranceExpiring ? 'text-orange-600 font-medium' : ''}>
                                                        Insurance: {new Date(item.insuranceTo).toLocaleDateString()}
                                                    </span>
                                                )}
                                                {item.registrationValidUpto && (
                                                    <span className={registrationExpiring ? 'text-orange-600 font-medium' : ''}>
                                                        Reg. Valid: {new Date(item.registrationValidUpto).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Shared With */}
                                        {item.sharedWith && item.sharedWith.length > 0 && (
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <div className="flex -space-x-2">
                                                    {item.sharedWith.slice(0, 3).map((member, index) => (
                                                        <div key={index} className="relative">
                                                            <MemberAvatar name={member} size="sm" className="border-2 border-white" />
                                                        </div>
                                                    ))}
                                                    {item.sharedWith.length > 3 && (
                                                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                            +{item.sharedWith.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Menu */}
                                        <div className="relative flex-shrink-0">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === item.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleEditVehicle(item)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteVehicle(item.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingVehicle ? 'Edit' : 'Add'} Vehicle
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Tab Navigation */}
                        <div className="border-b border-gray-200 px-6">
                            <div className="flex gap-6 overflow-x-auto">
                                <button
                                    onClick={() => setActiveSection('details')}
                                    className={`py-3 font-medium transition-colors whitespace-nowrap ${
                                        activeSection === 'details'
                                            ? 'text-primary-blue border-b-2 border-primary-blue'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Basic Details
                                </button>
                                <button
                                    onClick={() => setActiveSection('registration')}
                                    className={`py-3 font-medium transition-colors whitespace-nowrap ${
                                        activeSection === 'registration'
                                            ? 'text-primary-blue border-b-2 border-primary-blue'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Registration
                                </button>
                                <button
                                    onClick={() => setActiveSection('insurance')}
                                    className={`py-3 font-medium transition-colors whitespace-nowrap ${
                                        activeSection === 'insurance'
                                            ? 'text-primary-blue border-b-2 border-primary-blue'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Insurance
                                </button>
                                <button
                                    onClick={() => setActiveSection('service')}
                                    className={`py-3 font-medium transition-colors whitespace-nowrap ${
                                        activeSection === 'service'
                                            ? 'text-primary-blue border-b-2 border-primary-blue'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Service History
                                </button>
                                <button
                                    onClick={() => setActiveSection('accidents')}
                                    className={`py-3 font-medium transition-colors whitespace-nowrap ${
                                        activeSection === 'accidents'
                                            ? 'text-primary-blue border-b-2 border-primary-blue'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Accidents
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-5">
                            {activeSection === 'details' && (
                                <>
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="e.g., Honda City"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                    </div>

                                    {/* Vehicle Type */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type *</label>
                                        <select
                                            value={vehicleType}
                                            onChange={(e) => setVehicleType(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        >
                                            {vehicleTypes.map((type) => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Brand and Model in row */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                                            <input
                                                type="text"
                                                value={brand}
                                                onChange={(e) => setBrand(e.target.value)}
                                                placeholder="e.g., Honda"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                                            <input
                                                type="text"
                                                value={model}
                                                onChange={(e) => setModel(e.target.value)}
                                                placeholder="e.g., City VX"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Purchase Date */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                                        <input
                                            type="date"
                                            value={purchaseDate}
                                            onChange={(e) => setPurchaseDate(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                    </div>

                                    {/* Check Challans Link */}
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">Check Traffic Challans</span>
                                            <button
                                                type="button"
                                                onClick={() => alert('Check Challans feature coming soon!')}
                                                className="px-3 py-1 text-sm bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
                                            >
                                                Coming Soon
                                            </button>
                                        </div>
                                    </div>

                                    {/* Share With */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Share with Family Members (Default: All)
                                        </label>
                                        {getFamilyMembers().length > 0 ? (
                                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                                {getFamilyMembers().map((member, index) => (
                                                    <MemberSelector
                                                        key={index}
                                                        name={member.name}
                                                        photo={member.photo}
                                                        selected={sharedWith.includes(member.name)}
                                                            onChange={() => toggleMemberAccess(member)}
                                                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                        />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                <p className="text-sm text-gray-600">
                                                    No family members found. Add members to your family in the{' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            resetForm();
                                                            navigate('/famlis');
                                                        }}
                                                        className="text-primary-blue hover:underline font-medium"
                                                    >
                                                        Mi Famlis
                                                    </button>
                                                    {' '}page.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeSection === 'registration' && (
                                <>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Details</h3>

                                    {/* Registration Number */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Registration Number</label>
                                        <input
                                            type="text"
                                            value={registrationNumber}
                                            onChange={(e) => setRegistrationNumber(e.target.value)}
                                            placeholder="e.g., MH-01-AB-1234"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-mono"
                                        />
                                    </div>

                                    {/* Engine and Chassis Numbers */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Engine Number</label>
                                            <input
                                                type="text"
                                                value={engineNumber}
                                                onChange={(e) => setEngineNumber(e.target.value)}
                                                placeholder="Engine number"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-mono"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Chassis Number</label>
                                            <input
                                                type="text"
                                                value={chassisNumber}
                                                onChange={(e) => setChassisNumber(e.target.value)}
                                                placeholder="Chassis number"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent font-mono"
                                            />
                                        </div>
                                    </div>

                                    {/* Registration Valid Upto */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Registration Valid Upto</label>
                                        <input
                                            type="date"
                                            value={registrationValidUpto}
                                            onChange={(e) => setRegistrationValidUpto(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                    </div>

                                    {/* Registration Document */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Registration Document</label>
                                        <input
                                            ref={regFileInputRef}
                                            type="file"
                                            onChange={(e) => handleFileSelect('registration', e)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                        {registrationDocument && (
                                            <p className="mt-2 text-sm text-gray-600">Selected: {registrationDocument.name}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeSection === 'insurance' && (
                                <>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Insurance Details</h3>
                                    
                                    {/* Insured By */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Insured By</label>
                                        <input
                                            type="text"
                                            value={insuredBy}
                                            onChange={(e) => setInsuredBy(e.target.value)}
                                            placeholder="e.g., HDFC ERGO, ICICI Lombard"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                    </div>

                                    {/* Insurance From and To */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Insurance From</label>
                                            <input
                                                type="date"
                                                value={insuranceFrom}
                                                onChange={(e) => setInsuranceFrom(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Insurance To</label>
                                            <input
                                                type="date"
                                                value={insuranceTo}
                                                onChange={(e) => setInsuranceTo(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                            />
                                        </div>
                                    </div>

                                    {/* Premium Paid */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Premium Paid</label>
                                        <input
                                            type="text"
                                            value={premiumPaid}
                                            onChange={(e) => setPremiumPaid(e.target.value)}
                                            placeholder="e.g., ₹15,000"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                    </div>

                                    {/* Insurance Document */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Document</label>
                                        <input
                                            ref={insFileInputRef}
                                            type="file"
                                            onChange={(e) => handleFileSelect('insurance', e)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                        />
                                        {insuranceDocument && (
                                            <p className="mt-2 text-sm text-gray-600">Selected: {insuranceDocument.name}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeSection === 'service' && (
                                <>
                                    {/* Service Due Dates */}
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Due Dates</h3>
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm text-gray-600">Schedule upcoming service dates with recurring reminders</p>
                                            <button
                                                type="button"
                                                onClick={addServiceDate}
                                                className="text-sm text-primary-blue hover:text-blue-700 font-medium flex items-center gap-1 whitespace-nowrap"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Add Service Date
                                            </button>
                                        </div>
                                        <div className="space-y-3">
                                            {serviceDates.map((serviceDate, index) => (
                                                <div key={index} className="p-3 border border-gray-200 rounded-lg bg-blue-50/50">
                                                    <div className="flex items-start gap-2">
                                                        <div className="flex-1 grid grid-cols-2 gap-3">
                                                            <div>
                                                                <label className="block text-xs text-gray-600 mb-1">Service Date</label>
                                                                <input
                                                                    type="date"
                                                                    value={serviceDate.date}
                                                                    onChange={(e) => updateServiceDate(index, 'date', e.target.value)}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                                />
                                                            </div>
                                                            {serviceDate.date && (
                                                                <div>
                                                                    <label className="block text-xs text-gray-600 mb-1">Recurrence</label>
                                                                    <select
                                                                        value={serviceDate.recurrence}
                                                                        onChange={(e) => updateServiceDate(index, 'recurrence', e.target.value)}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                                    >
                                                                        {recurrenceOptions.map((option) => (
                                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {serviceDates.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeServiceDate(index)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 my-6"></div>

                                    {/* Service History Records */}
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Service History Records</h3>
                                        <button
                                            type="button"
                                            onClick={addServiceHistory}
                                            className="text-sm text-primary-blue hover:text-blue-700 font-medium flex items-center gap-1"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Service Record
                                        </button>
                                    </div>

                                    {serviceHistory.length === 0 ? (
                                        <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                                            <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-gray-500">No service history yet. Click "Add Service Record" to get started.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {serviceHistory.map((service) => (
                                                <div key={service.id} className="p-4 border border-gray-200 rounded-lg">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <h4 className="font-medium text-gray-900">Service Record</h4>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeServiceHistory(service.id)}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs text-gray-600 mb-1">Service Date</label>
                                                            <input
                                                                type="date"
                                                                value={service.date}
                                                                onChange={(e) => updateServiceHistory(service.id, 'date', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-600 mb-1">Cost</label>
                                                            <input
                                                                type="text"
                                                                value={service.cost}
                                                                onChange={(e) => updateServiceHistory(service.id, 'cost', e.target.value)}
                                                                placeholder="₹"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="block text-xs text-gray-600 mb-1">Service Provider</label>
                                                        <input
                                                            type="text"
                                                            value={service.serviceProvider}
                                                            onChange={(e) => updateServiceHistory(service.id, 'serviceProvider', e.target.value)}
                                                            placeholder="e.g., Authorized Service Center"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                                                        <textarea
                                                            value={service.description}
                                                            onChange={(e) => updateServiceHistory(service.id, 'description', e.target.value)}
                                                            placeholder="Service details..."
                                                            rows="2"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm resize-none"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {activeSection === 'accidents' && (
                                <>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Accidents Record</h3>
                                        <button
                                            type="button"
                                            onClick={addAccidentRecord}
                                            className="text-sm text-primary-blue hover:text-blue-700 font-medium flex items-center gap-1"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Accident Record
                                        </button>
                                    </div>

                                    {accidentsRecord.length === 0 ? (
                                        <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                                            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                            <p className="text-gray-500">No accident records. Click "Add Accident Record" if needed.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {accidentsRecord.map((accident) => (
                                                <div key={accident.id} className="p-4 border border-red-200 bg-red-50 rounded-lg">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <h4 className="font-medium text-red-900">Accident Record</h4>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAccidentRecord(accident.id)}
                                                            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs text-gray-700 mb-1">Date</label>
                                                            <input
                                                                type="date"
                                                                value={accident.date}
                                                                onChange={(e) => updateAccidentRecord(accident.id, 'date', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-700 mb-1">Severity</label>
                                                            <select
                                                                value={accident.severity}
                                                                onChange={(e) => updateAccidentRecord(accident.id, 'severity', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            >
                                                                <option value="Minor">Minor</option>
                                                                <option value="Moderate">Moderate</option>
                                                                <option value="Major">Major</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="block text-xs text-gray-700 mb-1">Location</label>
                                                        <input
                                                            type="text"
                                                            value={accident.location}
                                                            onChange={(e) => updateAccidentRecord(accident.id, 'location', e.target.value)}
                                                            placeholder="Accident location"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                        />
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="block text-xs text-gray-700 mb-1">Type of Accident</label>
                                                        <input
                                                            type="text"
                                                            value={accident.type}
                                                            onChange={(e) => updateAccidentRecord(accident.id, 'type', e.target.value)}
                                                            placeholder="e.g., Collision, Hit & Run"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                        />
                                                    </div>
                                                    <div className="mt-3 grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs text-gray-700 mb-1">Payment Type</label>
                                                            <select
                                                                value={accident.paymentType}
                                                                onChange={(e) => updateAccidentRecord(accident.id, 'paymentType', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            >
                                                                <option value="Insurance">Insurance</option>
                                                                <option value="Self-paid">Self-paid</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-700 mb-1">Impacted Parts</label>
                                                            <input
                                                                type="text"
                                                                value={accident.impactedParts}
                                                                onChange={(e) => updateAccidentRecord(accident.id, 'impactedParts', e.target.value)}
                                                                placeholder="e.g., Front bumper"
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <label className="block text-xs text-gray-700 mb-1">Description</label>
                                                        <textarea
                                                            value={accident.description}
                                                            onChange={(e) => updateAccidentRecord(accident.id, 'description', e.target.value)}
                                                            placeholder="Additional details..."
                                                            rows="2"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm resize-none"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddVehicle}
                                disabled={!name || !vehicleType}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingVehicle ? 'Update' : 'Add'} Vehicle
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Vehicles;

